/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Ionicons } from '@expo/vector-icons';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, ticketColors, ticketFonts } from '../theme/public-api';
import { useModalBackdropPadding } from '../ui/hooks-ui/useModalBackdropPadding';
import { TicketModalHeader } from '../ticket/TicketModalHeader';
import { modalExperience } from './modalExperience.styles';

const NARROW_MAX_WIDTH = 420;
/** Square: warn amber (not semantic.danger red) for destructive confirms. */
const CONFIRM_WARN = '#B45309';

export type OperationalConfirmModalProps = {
  visible: boolean;
  title: string;
  /** Kicker uppercase del header ticket (ej. "Gastos"). */
  kicker?: string;
  /** Subtítulo bajo el título (ej. "Distribuidora Sur · $48.500 · queda el registro anulado"). */
  subtitle?: string;
  /** Nombre del ítem, cliente, etc. */
  entityName?: string;
  message: string;
  /** Omite el bloque icono+mensaje — el body muestra solo `children` (variante form del proto). */
  hideMessage?: boolean;
  confirmLabel: string;
  loading?: boolean;
  destructive?: boolean;
  /** Segundos antes de habilitar confirmar (acciones destructivas). */
  confirmDelaySeconds?: number;
  iconName?: keyof typeof Ionicons.glyphMap;
  /** Deshabilita confirmar (ej. motivo demasiado corto). */
  confirmDisabled?: boolean;
  /** testID propio para el botón de confirmar (default `confirm-modal.confirm`). */
  confirmTestID?: string;
  /**
   * `overlay`: capa dentro de otro modal (detalle, etc.) sin segundo `Modal` nativo.
   * Evita que al cerrar la confirmación se cierre el modal de fondo en web.
   */
  presentation?: 'modal' | 'overlay';
  /** Contenido extra entre el mensaje y los botones (ej. campo de motivo). */
  children?: React.ReactNode;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

type ConfirmBodyProps = {
  message: string;
  /** Línea principal opcional (ej. pregunta de confirmación). */
  title?: string;
  destructive?: boolean;
  iconName?: keyof typeof Ionicons.glyphMap;
  children?: React.ReactNode;
  /** Clean design: icono centrado arriba, texto centrado (mobile). */
  cleanDesign?: boolean;
};

/** Icono + mensaje en un solo panel; reutilizable en modales de confirmación compuestos. */
export function OperationalConfirmBody({
  message,
  title,
  destructive = false,
  iconName,
  children,
  cleanDesign = false,
}: ConfirmBodyProps) {
  const resolvedIconName = iconName ?? (destructive ? 'alert-circle-outline' : 'information-circle-outline');
  const iconColor = destructive ? CONFIRM_WARN : ticketColors.gold;

  if (cleanDesign) {
    return (
      <View style={styles.cleanBody}>
        <View
          style={[
            styles.cleanIconWrap,
            destructive ? styles.cleanIconWrapDanger : styles.cleanIconWrapAccent,
          ]}
        >
          <Ionicons name={resolvedIconName} size={24} color={iconColor} />
        </View>
        {title ? <Text style={styles.cleanBodyTitle}>{title}</Text> : null}
        <Text
          style={[styles.cleanBodyMessage, destructive && styles.cleanBodyMessageDanger]}
        >
          {message}
        </Text>
        {children ? <View style={styles.cleanChildren}>{children}</View> : null}
      </View>
    );
  }

  return (
    <View
      style={[
        modalExperience.confirmBodyPanel,
        destructive ? modalExperience.confirmBodyPanelDanger : null,
      ]}
    >
      <View style={[modalExperience.confirmIcon, destructive ? modalExperience.confirmIconDanger : null]}>
        <Ionicons name={resolvedIconName} size={22} color={iconColor} />
      </View>
      <View style={modalExperience.confirmBodyText}>
        {title ? <Text style={modalExperience.confirmBodyTitle}>{title}</Text> : null}
        <Text
          style={[
            modalExperience.confirmBodyMessage,
            destructive ? modalExperience.confirmBodyMessageDanger : null,
          ]}
        >
          {message}
        </Text>
        {children}
      </View>
    </View>
  );
}

function OperationalConfirmPanel({
  title,
  kicker,
  subtitle,
  entityName,
  message,
  hideMessage = false,
  confirmLabel,
  loading = false,
  destructive = false,
  confirmDelaySeconds = 0,
  iconName,
  confirmDisabled = false,
  confirmTestID,
  children,
  onClose,
  onConfirm,
}: Omit<OperationalConfirmModalProps, 'visible' | 'presentation'>) {
  const { width } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web';
  const narrow = width < NARROW_MAX_WIDTH;
  const useSheet = isMobile || narrow;
  const insets = useSafeAreaInsets();
  const [delayRemaining, setDelayRemaining] = useState(0);

  useEffect(() => {
    if (confirmDelaySeconds <= 0) {
      setDelayRemaining(0);
      return;
    }
    setDelayRemaining(confirmDelaySeconds);
    const timer = setInterval(() => {
      setDelayRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [confirmDelaySeconds, title, entityName, message]);

  const confirmLocked = delayRemaining > 0;
  const confirmBlocked = loading || confirmLocked || confirmDisabled;
  const resolvedConfirmLabel =
    confirmLocked && !loading ? `${confirmLabel} (${delayRemaining}s)` : confirmLabel;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  if (useSheet) {
    return (
      <View
        style={[
          styles.sheet,
          { paddingBottom: Math.max(insets.bottom, spacing.sm) },
        ]}
      >
        {/* Grabber */}
        <View style={styles.grabber} />

        {/* Sheet header */}
        <TicketModalHeader title={title} kicker={kicker} subtitle={subtitle} onClose={handleClose} closeDisabled={loading} compact borderless />

        {/* Body */}
        <View style={styles.sheetBody}>
          {hideMessage ? (
            <View style={styles.hideMessageBody}>{children}</View>
          ) : (
            <OperationalConfirmBody
              message={message}
              destructive={destructive}
              iconName={iconName}
              cleanDesign
            >
              {children}
            </OperationalConfirmBody>
          )}
        </View>

        {/* Footer */}
        <View style={styles.sheetFooter}>
          <TouchableOpacity
            style={styles.sheetBtnCancel}
            onPress={handleClose}
            disabled={loading}
            accessibilityRole="button"
          >
            <Text style={styles.sheetBtnCancelText} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              destructive ? styles.sheetBtnDanger : styles.sheetBtnPrimary,
              (loading || confirmLocked || confirmDisabled) && styles.sheetBtnDisabled,
            ]}
            onPress={() => void onConfirm()}
            disabled={confirmBlocked}
            accessibilityRole="button"
            accessibilityLabel={confirmLabel}
            accessibilityState={{ disabled: confirmBlocked }}
            testID={confirmTestID ?? 'confirm-modal.confirm'}
          >
            <Text
              style={destructive ? styles.sheetBtnDangerText : styles.sheetBtnPrimaryText}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
            >
              {loading ? 'Procesando…' : resolvedConfirmLabel}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.deskCard, styles.cardMax]}>
      <TicketModalHeader title={title} kicker={kicker} subtitle={subtitle} onClose={handleClose} closeDisabled={loading} borderless />
      {entityName ? <Text style={styles.deskEntity}>{entityName}</Text> : null}
      <View style={styles.deskBody}>
        {hideMessage ? (
          <View style={styles.hideMessageBody}>{children}</View>
        ) : (
          <OperationalConfirmBody message={message} destructive={destructive} iconName={iconName} cleanDesign>
            {children}
          </OperationalConfirmBody>
        )}
      </View>
      <View style={styles.deskFooter}>
        <TouchableOpacity
          style={styles.sheetBtnCancel}
          onPress={handleClose}
          disabled={loading}
          accessibilityRole="button"
        >
          <Text style={styles.sheetBtnCancelText} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8}>Cancelar</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            destructive ? styles.sheetBtnDanger : styles.sheetBtnPrimary,
            confirmBlocked && styles.sheetBtnDisabled,
          ]}
          onPress={() => void onConfirm()}
          disabled={confirmBlocked}
          accessibilityRole="button"
          accessibilityLabel={confirmLabel}
          accessibilityState={{ disabled: confirmBlocked }}
          testID={confirmTestID ?? 'confirm-modal.confirm'}
        >
          <Text
            style={destructive ? styles.sheetBtnDangerText : styles.sheetBtnPrimaryText}
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.7}
          >
            {loading ? 'Procesando…' : resolvedConfirmLabel}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

export function OperationalConfirmModal({
  visible,
  presentation = 'modal',
  onClose,
  confirmDelaySeconds = 0,
  ...panelProps
}: OperationalConfirmModalProps) {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web';
  const narrow = width < NARROW_MAX_WIDTH;
  const useSheet = isMobile || narrow;

  if (!visible) return null;

  const handleClose = () => {
    if (panelProps.loading) return;
    onClose();
  };

  if (presentation === 'overlay') {
    return (
      <View style={useSheet ? styles.overlaySheetRoot : modalExperience.confirmOverlayRoot} pointerEvents="box-none">
        <Pressable
          style={useSheet ? styles.overlaySheetBackdrop : modalExperience.confirmOverlayBackdrop}
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar confirmación"
        />
        <View
          style={useSheet ? styles.overlaySheetHost : styles.overlayCardHost}
          onStartShouldSetResponder={() => true}
        >
          <OperationalConfirmPanel
            {...panelProps}
            confirmDelaySeconds={confirmDelaySeconds}
            onClose={onClose}
          />
        </View>
      </View>
    );
  }

  return (
    <Modal visible transparent animationType={useSheet ? 'slide' : 'fade'} onRequestClose={handleClose}>
      <View
        style={
          useSheet
            ? styles.sheetBackdrop
            : [styles.deskBackdrop, modalBackdropPadding]
        }
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={useSheet ? styles.sheetPanelWrap : styles.cardPanelWrap}
          pointerEvents="box-none"
        >
          <OperationalConfirmPanel
            {...panelProps}
            confirmDelaySeconds={confirmDelaySeconds}
            onClose={onClose}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  // ── Desktop card (wide screens) — Square paper dialog ──
  card: {
    gap: spacing.sm,
  },
  cardMax: {
    maxWidth: 420,
    width: '100%',
  },
  deskCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 12px 40px rgba(15,20,18,0.12)' } as const)
      : {
          shadowColor: '#0F1412',
          shadowOpacity: 0.12,
          shadowRadius: 20,
          shadowOffset: { width: 0, height: 8 },
          elevation: 8,
        }),
  },
  deskEntity: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    paddingHorizontal: 18,
    marginBottom: 4,
  },
  deskBody: {
    paddingHorizontal: 18,
    paddingBottom: 8,
    alignItems: 'center',
  },
  hideMessageBody: {
    width: '100%',
    gap: spacing.sm,
    alignItems: 'stretch',
  },
  deskFooter: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  overlayCardHost: {
    width: '100%',
    maxWidth: 420,
    zIndex: 1,
  },

  // ── Bottom sheet backdrop (mobile/narrow) ──
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,20,18,0.45)',
    justifyContent: 'flex-end',
  },
  deskBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,20,18,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  sheetPanelWrap: {
    width: '100%',
    zIndex: 1,
  },

  // ── Desktop card backdrop wrapper ──
  cardPanelWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    width: '100%',
  },

  // ── Bottom sheet ──
  sheet: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    width: '100%',
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(28,25,23,0.14)',
    alignSelf: 'center',
    marginTop: 4,
    marginBottom: 12,
  },
  sheetBody: {
    paddingHorizontal: 18,
    paddingBottom: spacing.md,
    alignItems: 'center',
  },

  // ── Clean design body (centered icon + text) ──
  cleanBody: {
    alignItems: 'center',
    gap: spacing.sm,
    width: '100%',
  },
  cleanIconWrap: {
    width: 52,
    height: 52,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cleanIconWrapDanger: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
  },
  cleanIconWrapAccent: {
    borderColor: ticketColors.brandSoft,
    backgroundColor: ticketColors.brandSoft,
  },
  cleanBodyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.ink,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
  },
  cleanBodyMessage: {
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink2,
    textAlign: 'center',
    lineHeight: 20,
    fontFamily: ticketFonts.sans,
  },
  cleanBodyMessageDanger: {
    color: ticketColors.ink,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  cleanChildren: {
    width: '100%',
    gap: spacing.sm,
    alignItems: 'stretch',
  },

  // ── Sheet footer ──
  sheetFooter: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: 18,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  sheetBtnCancel: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetBtnCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  sheetBtnPrimary: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetBtnPrimaryText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: ticketFonts.sans,
  },
  sheetBtnDanger: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    backgroundColor: CONFIRM_WARN,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetBtnDangerText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: ticketFonts.sans,
  },
  sheetBtnDisabled: {
    opacity: 0.5,
  },

  // ── Overlay sheet (inside another modal, mobile) ──
  overlaySheetRoot: {
    ...StyleSheet.absoluteFill,
    zIndex: 9999,
    elevation: 9999,
    justifyContent: 'flex-end',
  },
  overlaySheetBackdrop: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(15,20,18,0.45)',
  },
  overlaySheetHost: {
    width: '100%',
    zIndex: 1,
  },
});
